import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getVouchers, getVoucherTransactions } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import SearchInput from "../components/UI/SearchInput";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// VoucherDetailModal is this screen's own read-only drill-down — the full
// append-only ledger behind one voucher's Balance (see VoucherTransaction),
// oldest first so it reads like a bank statement.
function VoucherDetailModal({ voucher, onClose }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("vouchers");
  const { t: tCommon } = useTranslation("common");
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const currencyLabel = voucher.currency?.symbol || voucher.currency?.code || "";

  const TXN_TYPE_LABELS = {
    issue: t("txn_type_issue", "Issued"),
    payment: t("txn_type_payment", "Spent"),
    forfeit: t("txn_type_forfeit", "Forfeited"),
  };

  useEffect(() => {
    getVoucherTransactions(token, voucher.id)
      .then((rows) => setTxns(rows || []))
      .catch(() => notify.error(t("notify_load_history_failed", "Failed to load voucher history")))
      .finally(() => setLoading(false));
  }, [voucher.id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-lg font-bold text-gray-800 font-mono">{voucher.code}</h3>
            <p className="text-xs text-gray-400">
              {voucher.customer ? voucher.customer.name : t("modal_customer_anonymous", "Anonymous (bearer) voucher")}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-red-500">
            <ICONS.Close className="w-6 h-6" />
          </button>
        </div>

        <div className="flex justify-between items-center bg-gray-50/60 rounded-2xl p-4 my-4 text-sm">
          <span className="text-gray-500">{t("modal_balance_label", "Balance")}</span>
          <span className={`font-mono font-black text-lg ${voucher.balance > 0 ? "text-emerald-600" : "text-gray-400"}`}>
            {money(voucher.balance)} / {money(voucher.initial_amount)} {currencyLabel}
          </span>
        </div>

        {/* Only a return-issued voucher can ever carry an expiry at all
            (see PurchaseVoucher.ExpiresAt's own doc comment) — null here
            just means "open-ended", not "unknown". */}
        <div className="flex justify-between items-center text-xs mb-4 px-1">
          <span className="text-gray-400">{t("col_expires", "Expires")}</span>
          <span className={voucher.expires_at && new Date(voucher.expires_at) < new Date() ? "font-bold text-red-500" : "font-medium text-gray-600"}>
            {voucher.expires_at ? new Date(voucher.expires_at).toLocaleDateString() : t("label_no_expiry", "No expiry")}
          </span>
        </div>

        <h4 className="text-xs uppercase font-bold text-gray-400 tracking-wide mb-2">{t("modal_history_label", "History")}</h4>
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-6">{t("modal_loading", "Loading…")}</p>
        ) : txns.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">{t("modal_no_transactions", "No transactions yet")}</p>
        ) : (
          <div className="space-y-2">
            {txns.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between bg-gray-50/60 rounded-xl px-3 py-2 text-xs">
                <div>
                  <span className="font-bold text-gray-700">{TXN_TYPE_LABELS[tx.type] || tx.type}</span>
                  {tx.order_id && <span className="text-gray-400"> {t("modal_order_ref", "— Order #{id}").replace("{id}", tx.order_id)}</span>}
                  {tx.payment_method && <span className="text-gray-400"> — {tx.payment_method}</span>}
                  <div className="text-[10px] text-gray-400 italic">{new Date(tx.created_at).toLocaleString()} by {tx.created_by?.username || `#${tx.created_by_id}`}</div>
                </div>
                <span className={`font-mono font-bold ${tx.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {tx.amount >= 0 ? "+" : ""}{money(tx.amount)}
                </span>
              </div>
            ))}
          </div>
        )}

        <Button variant="secondary" onClick={onClose} className="w-full mt-6">{tCommon("btn_close", "Close")}</Button>
      </div>
    </div>
  );
}

export default function Vouchers() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("vouchers");
  const { t: tCommon } = useTranslation("common");

  const STATUS_OPTIONS = [
    { value: "", label: t("status_option_all", "All statuses") },
    { value: "active", label: t("status_option_active", "Active") },
    { value: "exhausted", label: t("status_option_exhausted", "Exhausted") },
  ];

  const [vouchers, setVouchers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [viewingVoucher, setViewingVoucher] = useState(null);

  const fetchVouchers = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await getVouchers(token, params);
      setVouchers(data || []);
    } catch (err) {
      notify.error(t("notify_load_failed", "Failed to load vouchers"));
    }
  };

  // Re-fetches on the status filter (server-side) only — free-text search
  // is filtered client-side below, same idiom Customers.js already uses,
  // so typing doesn't fire a request per keystroke.
  useEffect(() => { fetchVouchers(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedAndFiltered = useMemo(() => {
    let result = vouchers.filter((v) => {
      const term = searchTerm.toLowerCase();
      if (!term) return true;
      return (
        v.code?.toLowerCase().includes(term) ||
        v.customer?.name?.toLowerCase().includes(term) ||
        v.customer?.phone?.toLowerCase().includes(term)
      );
    });
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [vouchers, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    { key: "code", label: t("col_code", "Code"), className: "text-gray-800 font-mono font-bold" },
    {
      key: "customer",
      label: t("col_customer", "Customer"),
      className: "text-gray-600",
      render: (row) => row.customer ? row.customer.name : <span className="italic text-gray-400">{t("badge_anonymous", "Anonymous")}</span>,
    },
    {
      key: "balance",
      label: t("col_balance_initial", "Balance / Initial"),
      className: "text-gray-700 font-mono",
      render: (row) => `${money(row.balance)} / ${money(row.initial_amount)} ${row.currency?.symbol || row.currency?.code || ""}`,
    },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => {
        // Expired overrides Active visually even though the backend's own
        // ?status=active filter (ListVouchersHandler) still matches this
        // row on balance alone — CreateOrderHandler is what actually
        // blocks redemption once ExpiresAt has passed, so a leftover
        // balance on an expired voucher is real but unspendable, and the
        // badge here should say so rather than just "Active".
        const expired = row.balance > 0 && row.expires_at && new Date(row.expires_at) < new Date();
        if (expired) {
          return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-50 text-red-500">{t("status_option_expired", "Expired")}</span>;
        }
        return (
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.balance > 0 ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
            {row.balance > 0 ? t("status_option_active", "Active") : t("status_option_exhausted", "Exhausted")}
          </span>
        );
      },
    },
    {
      key: "expires_at",
      label: t("col_expires", "Expires"),
      className: "text-gray-500",
      render: (row) => {
        if (!row.expires_at) return <span className="italic text-gray-400">{t("label_no_expiry", "No expiry")}</span>;
        const expired = new Date(row.expires_at) < new Date();
        return <span className={expired ? "font-bold text-red-500" : ""}>{new Date(row.expires_at).toLocaleDateString()}</span>;
      },
    },
    { key: "issued_by", label: t("col_issued_by", "Issued By"), className: "text-gray-500", render: (row) => row.issued_by?.username || `#${row.issued_by_id}` },
    { key: "created_at", label: t("col_created_at", "Created"), className: "text-gray-500", render: (row) => new Date(row.created_at).toLocaleString() },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      {viewingVoucher && <VoucherDetailModal voucher={viewingVoucher} onClose={() => setViewingVoucher(null)} />}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Vouchers size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Purchase Vouchers")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every voucher ever issued — from a return or sold directly at Checkout")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Checkout} onClick={() => navigate("/checkout")} className="w-full sm:w-auto shrink-0">
          {t("btn_go_to_checkout", "Go to Checkout")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <SearchInput value={searchTerm} onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }} placeholder={t("search_placeholder", "Search by code or customer…")} />
        <SelectBox label={t("filter_status_label", "Filter by status")} options={STATUS_OPTIONS} value={statusFilter} onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row) => (
          <Button variant="secondary" size="sm" icon={ICONS.Vouchers} onClick={() => setViewingVoucher(row)}>{t("btn_view", "View")}</Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
            <h3 className="font-bold text-gray-800">{money(row.balance)}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
